import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  // Page top par scroll karne ke liye on load
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Animation settings for repeat performance
  const reveal = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6, ease: "easeOut" } 
    }
  };

  return (
    <div className="privacy-container">
      <motion.div 
        className="privacy-card"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="privacy-header">
          <motion.h1 variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
            PRIVACY POLICY
          </motion.h1>
          <div className="brand-accent-line"></div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
             <h2 className="brand-name-top">Chandrama Holidays</h2>
             <p className="brand-tagline">A travel you’ll remember for a lifetime.</p>
          </motion.div>
        </div>

        <div className="privacy-content">
          {/* Section 1 */}
          <motion.section className="p-section" variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
            <h3>1. Overview</h3>
            <p>At Chandrama Holidays, we are committed to protecting your privacy and handling your personal information responsibly. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you interact with us through our website, phone, WhatsApp, email, or during bookings.</p>
            <p>We value the trust you place in Chandrama Holidays and ensure that your information is handled with transparency and care.</p>
            <p>By using our services, you agree to the practices described in this policy.</p>
          </motion.section>

          {/* Section 2 */}
          <motion.section className="p-section" variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
            <h3>2. Information We Collect</h3>
            <p>We may collect the following information:</p>
            
            <div className="sub-section-box orange-box">
              <h4>A. Personal Information</h4>
              <ul>
                <li>Full name</li>
                <li>Contact number</li>
                <li>Email address</li>
                <li>Residential address</li>
                <li>Government-issued ID details (when required for bookings)</li>
                <li>Passport details (for international travel)</li>
              </ul>
            </div>

            <div className="sub-section-box blue-box">
              <h4>B. Booking & Travel Information</h4>
              <ul>
                <li>Travel dates</li>
                <li>Destination preferences</li>
                <li>Accommodation preferences</li>
                <li>Special requirements</li>
              </ul>
            </div>

            <div className="sub-section-box green-box">
              <h4>C. Payment Information</h4>
              <p>Payments are processed through secure third-party payment gateways. We do not store complete card or banking details on our servers.</p>
            </div>

            <div className="sub-section-box orange-box">
              <h4>D. Website Usage Data</h4>
              <ul>
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Pages visited</li>
              </ul>
              <p className="small-text">This data helps us improve user experience and website performance.</p>
            </div>
          </motion.section>

          {/* Section 3 */}
          <motion.section className="p-section" variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
            <h3>3. How We Use Your Information</h3>
            <p>We use collected information to:</p>
            <ul className="check-list">
              <li>Process and confirm bookings</li>
              <li>Coordinate services with hotels, transport providers, airlines, and vendors</li>
              <li>Provide itinerary updates and travel support</li>
              <li>Contact you via call, WhatsApp, email, or SMS regarding enquiries or bookings</li>
              <li>Improve our services and customer experience</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Share promotional offers (only with your consent)</li>
            </ul>
            <p><strong>We do not sell, rent, or trade your personal data.</strong></p>
            <p>We retain personal data only for as long as necessary for booking, legal, and operational purposes.</p>
          </motion.section>

          {/* Section 4 & 5 */}
          <motion.section className="p-section" variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
            <h3>4. Data Sharing</h3>
            <p>Your information may be shared only:</p>
            <ul>
              <li>With trusted service providers strictly for booking execution</li>
              <li>When required by law or government authorities</li>
              <li>In case of business restructuring or transfer</li>
            </ul>
            <p>All third-party partners are expected to maintain confidentiality.</p>

            <h3>5. Data Security</h3>
            <p>We implement reasonable administrative and technical safeguards to protect your personal information against unauthorized access, misuse, or disclosure.</p>
            <p><i>However, no online transmission is 100% secure, and we cannot guarantee absolute security.</i></p>
          </motion.section>

          {/* Section 6 & 7 */}
          <motion.section className="p-section" variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
            <h3>6. Your Rights</h3>
            <p>You may:</p>
            <ul>
              <li>Request access to your data</li>
              <li>Request correction of incorrect data</li>
              <li>Request deletion of your data (subject to legal requirements)</li>
              <li>Opt out of promotional communications at any time</li>
            </ul>
            <p>Requests can be made via email.</p>

            <h3>7. Cookies</h3>
            <p>Our website may use cookies to enhance user experience and analyze website traffic. You may disable cookies in your browser settings if preferred.</p>
          </motion.section>

          {/* Section 8 - Redirects */}
          <motion.div className="contact-card-premium" variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
            <h3>8. Contact Information</h3>
            <p>For any privacy-related questions:</p>
            <div className="contact-details-wrap">
              <h4 className="c-name">Chandrama Holidays</h4>
              <a href="https://maps.google.com/?q=Shakti+Chambers+Sangamvadi+Pune+411003" target="_blank" rel="noreferrer" className="contact-link-item">
                📍 Pune, Maharashtra, India
              </a>
              <a href="mailto:chandramaholidays12@gmail.com" className="contact-link-item">
                📧 chandramaholidays12@gmail.com
              </a>
            </div>
          </motion.div>

          <motion.div className="closing-footer" variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: false }}>
            <p>Thank you for choosing Chandrama Holidays.</p>
            <p className="final-line">We look forward to planning a journey you’ll remember for a lifetime.</p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;